import React from 'react';

import { CheckCircleOutlined, LockOutlined } from '@ant-design/icons';
import { Card, Checkbox, Typography, Empty, Alert, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  ACTIONS,
  RESOURCES_BY_ROLE,
} from '../../../permissions_manifest.generated';

const { Title, Text } = Typography;

/**
 * 權限顯示組件
 * 用於顯示用戶或 Operator 的權限列表（表格樣式，與 PermissionsEditor 一致）
 *
 * @param {String} role - 'admin' 或 'operator'，決定要列哪些 resource
 */
const PermissionsDisplay = ({ permissions = [], showTitle = true, role = 'admin' }) => {
  const { t } = useTranslation();

  const availableResources = RESOURCES_BY_ROLE[role] || [];
  const availableActions = ACTIONS;

  // 解析權限格式 "resource:action"
  const parsePermissions = () => {
    const parsed = {
      superAdmin: false,
      resources: {},
    };

    permissions.forEach((perm) => {
      if (perm === '*:*') {
        parsed.superAdmin = true;
        return;
      }

      const [resource, action] = perm.split(':');

      if (resource === '*') {
        // 全局 action - 暫時忽略
        return;
      }

      if (!parsed.resources[resource]) {
        parsed.resources[resource] = { all: false, actions: {} };
      }

      if (action === '*') {
        parsed.resources[resource].all = true;
      } else {
        parsed.resources[resource].actions[action] = true;
      }
    });

    return parsed;
  };

  const parsedPermissions = parsePermissions();

  // 檢查權限是否被勾選
  const isPermissionChecked = (permission) => {
    // 1. 直接包含該權限
    if (permissions.includes(permission)) {
      return true;
    }

    // 2. 檢查是否有超級管理員權限
    if (permissions.includes('*:*')) {
      return true;
    }

    // 3. 檢查父權限
    const [resource, action] = permission.split(':');

    // 如果有 resource:* 權限
    if (resource !== '*' && permissions.includes(`${resource}:*`)) {
      return true;
    }

    // 如果有 *:action 權限
    if (action !== '*' && permissions.includes(`*:${action}`)) {
      return true;
    }

    return false;
  };

  // 獲取權限說明文字 (Tooltip 用)
  const getTooltipText = (resource, action) => {
    const resourceName = t(`common.resources.${resource}`, resource);
    return t(`common.permission_tooltips.${action}`, { resource: resourceName });
  };

  if (permissions.length === 0) {
    return (
      <Card>
        {showTitle && (
          <Title level={4}>
            <LockOutlined /> {t('common.permission_labels.title')}
          </Title>
        )}
        <Empty
          description={t('common.permission_labels.no_permissions')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <Card>
      {showTitle && (
        <Title level={4}>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />{' '}
          {t('common.permission_labels.title')}
        </Title>
      )}

      {/* 超級管理員 */}
      {parsedPermissions.superAdmin && (
        <Alert
          message={t('common.permission_labels.super_admin')}
          description={t('common.permission_labels.super_admin_desc')}
          type="success"
          icon={<CheckCircleOutlined />}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 資源權限表格 */}
      {!parsedPermissions.superAdmin && availableResources.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                <th style={{ textAlign: 'left', padding: '12px 8px', minWidth: '180px' }}>
                  {t('common.permission_labels.resources')}
                </th>
                {availableActions.map(action => (
                  <th key={action} style={{ textAlign: 'center', padding: '12px 8px', minWidth: '70px' }}>
                    {t(`common.permission_actions.${action}`, action)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {availableResources.map((resource, index) => (
                <tr
                  key={resource}
                  style={{
                    borderBottom: '1px solid #f0f0f0',
                    backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff'
                  }}
                >
                  {/* 功能名稱（含全選 checkbox） */}
                  <td style={{ padding: '10px 8px' }}>
                    <Tooltip title={getTooltipText(resource, 'all')}>
                      <Checkbox
                        checked={isPermissionChecked(`${resource}:*`)}
                        disabled
                      >
                        <Text strong>{t(`common.resources.${resource}`, resource)}</Text>
                      </Checkbox>
                    </Tooltip>
                  </td>
                  {/* 各別動作 */}
                  {availableActions.map(action => {
                    const permission = `${resource}:${action}`;
                    return (
                      <td key={action} style={{ textAlign: 'center', padding: '10px 8px' }}>
                        <Tooltip title={getTooltipText(resource, action)}>
                          <Checkbox
                            checked={isPermissionChecked(permission)}
                            disabled
                          />
                        </Tooltip>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default PermissionsDisplay;
